import { onMessage } from 'firebase/messaging'
import dayjs from 'dayjs'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import screenfull from 'screenfull'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { ROLE_CA_FLOWS } from 'src/consumer/constants'
import { appVersion, platformWeb } from 'src/consumer/services'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { DocSideContext } from 'src/context/DocSideContextProvider'
import { messaging } from 'src/firebase'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import CorrectLayoutHandler from './components/CorrectLayoutHandler'
import {
  getActiveMentorsInAClinicLocation,
  getAndSetBookedSlotsOfMentorAtSelectedDate,
  getMentorListForClinicAssistant,
  getPrefixRemovedAppointmentNumber,
  getScreenersWithTheirBookedSlots,
  getUpdatedActiveAppointmentMapAndAnnounceForChanges,
} from './utils'

export default function ClinicCounterForMentor() {
  const params = useParams()
  // Kiosk route uses :location and :mentor; clinic/ca-home route uses :clinicLocationSlug and :mentorSlug
  const clinicId = params.clinicId
  const mentorId = params.mentorId
  const clinicLocationSlug = params.clinicLocationSlug ?? params.location
  const mentorSlug = params.mentorSlug ?? params.mentor
  const clinicDataContext = useContext(ClinicDataContext)
  const { assistantData } = useContext(ClinicDataContext)
  const { setFlowId } = useContext(DocSideContext)
  const navigate = useNavigate()
  
  // Prevent /ca-home/display/... routes - display route is only for /kiosk/
  React.useEffect(() => {
    const currentPath = window.location.pathname
    if (currentPath.startsWith('/ca-home/display/')) {
      // Redirect to /ca-home if someone tries to access display route from CA portal
      navigate('/ca-home', { replace: true })
      return
    }
  }, [navigate])
  
  // Resolve slugs to IDs if using slug-based route (only for /kiosk/)
  const resolvedClinicIdFromContext = React.useMemo(() => {
    const currentPath = window.location.pathname
    // Only resolve slugs if we're in kiosk portal
    if (currentPath.startsWith('/kiosk') && clinicLocationSlug) {
      const location = assistantData?.associatedClinicsData?.find((loc) => loc?.slug === clinicLocationSlug)
      return location?.id || null
    }
    return clinicId
  }, [clinicLocationSlug, clinicId, assistantData])

  // Kiosk display: context has empty associatedClinicsData, so resolve location via kiosk clinics API
  const [kioskResolvedClinicId, setKioskResolvedClinicId] = useState(null)
  const [kioskResolvedClinicData, setKioskResolvedClinicData] = useState(null)
  const [_kioskClinicsLoading, setKioskClinicsLoading] = useState(false)

  React.useEffect(() => {
    const currentPath = window.location.pathname
    if (
      !currentPath.startsWith('/kiosk') ||
      !clinicLocationSlug ||
      resolvedClinicIdFromContext != null
    ) {
      if (!currentPath.startsWith('/kiosk')) {
        setKioskResolvedClinicId(null)
        setKioskResolvedClinicData(null)
      }
      return
    }
    let cancelled = false
    setKioskClinicsLoading(true)
    getCPDataWithoutParams('/api/secure/kiosk/clinics')
      .then((response) => {
        if (cancelled) return
        if (response?.status === 200 && response?.data?.code === 2000) {
          const brandsData = response?.data?.data?.brands || []
          const allLocations = []
          brandsData.forEach((brand) => {
            if (Array.isArray(brand?.locations)) {
              allLocations.push(...brand.locations)
            }
          })
          const location =
            allLocations.find(
              (loc) => loc?.slug === clinicLocationSlug || loc?.id === clinicLocationSlug,
            ) || null
          if (location) {
            setKioskResolvedClinicId(location.id)
            setKioskResolvedClinicData(location)
          } else {
            setKioskResolvedClinicId(null)
            setKioskResolvedClinicData(null)
          }
        } else {
          setKioskResolvedClinicId(null)
          setKioskResolvedClinicData(null)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setKioskResolvedClinicId(null)
          setKioskResolvedClinicData(null)
        }
      })
      .finally(() => {
        if (!cancelled) setKioskClinicsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [clinicLocationSlug, resolvedClinicIdFromContext])

  const resolvedClinicId = resolvedClinicIdFromContext || kioskResolvedClinicId

  // All state declarations first
  const [resolvedMentorIdState, setResolvedMentorIdState] = useState(mentorId || null)
  const [selectedDate, setSelectedDate] = useState(dayjs().format('DD-MM-YYYY'))
  const [timeNow, setTimeNow] = useState(dayjs())
  const [bookedSlotsInDayForPrimaryMentor, setBookedSlotsInDayForPrimaryMentor] = useState([])
  const [bookedSlotsInDayLoading, setBookedSlotsInDayLoading] = useState(false)
  const [mentorDataLoading, setMentorDataLoading] = useState(false)
  const [mentorProfileData, setMentorProfileData] = useState({})
  const [primaryMentorCheckedIn, setPrimaryMentorCheckedIn] = useState(false)
  const [screeningMentorDataLoading, setScreeningMentorDataLoading] = useState(false)
  const [screeningMentorsWithBookingData, setScreeningMentorsWithBookingData] = useState([])
  const [OOHDataLoading, setOOHDataLoading] = useState(false)
  const [OOHData, setOOHData] = useState([])
  const [activeAppointmentMapForMentors, setActiveAppointmentMapForMentors] = useState({})
  const [oohConfig, setOohConfig] = useState()

  const timeZone = encodeURIComponent(getTimezoneOffset())
  const selectedDateStartTime = dayjs(selectedDate, 'DD-MM-YYYY')?.format(
    `YYYY-MM-DDTHH:mm:ss[${decodeURIComponent(timeZone)}]`,
  )
  const selectedDateEndTime = dayjs(selectedDate, 'DD-MM-YYYY')
    ?.endOf('day')
    ?.format(`YYYY-MM-DDTHH:mm:ss[${decodeURIComponent(timeZone)}]`)
  const selectedDateUtcStartTime = dayjs
    ?.utc(selectedDateStartTime)
    ?.format(`YYYY-MM-DDTHH:mm:ss[Z]`)
  const selectedDateUtcEndTime = dayjs?.utc(selectedDateEndTime)?.format(`YYYY-MM-DDTHH:mm:ss[Z]`)

  const resolvedMentorId = resolvedMentorIdState
  
  // Resolve mentor slug to ID when mentorProfileData is loaded (only for kiosk portal)
  React.useEffect(() => {
    const currentPath = window.location.pathname
    // Only resolve slugs if we're in kiosk portal
    if (currentPath.startsWith('/kiosk')) {
      if (mentorProfileData?.id) {
        setResolvedMentorIdState(mentorProfileData.id)
      } else if (mentorId) {
        setResolvedMentorIdState(mentorId)
      }
    } else {
      // For CA portal, just use mentorId directly
      if (mentorId) {
        setResolvedMentorIdState(mentorId)
      }
    }
  }, [mentorProfileData?.id, mentorId])

  // Fetch OOH content and config once we have both clinic and mentor IDs
  React.useEffect(() => {
    const currentMentorId = resolvedMentorIdState || mentorProfileData?.id
    if (resolvedClinicId && currentMentorId) {
      getOOHContent()
      getOohConfig()
    }
  }, [resolvedClinicId, resolvedMentorIdState, mentorProfileData?.id])

  async function getAndSetBookedSlotsOfSelectedDateForPrimaryMentor(mentorProfileData) {
    const currentMentorId = resolvedMentorIdState || mentorProfileData?.id
    if (currentMentorId && resolvedClinicId) {
      let response = await getAndSetBookedSlotsOfMentorAtSelectedDate({
        screeningMentor: { id: currentMentorId },
        selectedDateUtcStartTime,
        selectedDateUtcEndTime,
        clinicId: resolvedClinicId,
      })
      setBookedSlotsInDayForPrimaryMentor(response)
      if (mentorProfileData && Object.keys(mentorProfileData)?.length > 0) {
        setActiveAppointmentMapForMentors((prev) =>
          getUpdatedActiveAppointmentMapAndAnnounceForChanges(
            prev,
            currentMentorId,
            mentorProfileData?.name || mentorProfileData?.firstName,
            getPrefixRemovedAppointmentNumber(response?.[0]?.appointmentNumber),
            getPrefixRemovedAppointmentNumber(response?.[1]?.appointmentNumber),
            primaryMentorCheckedIn,
            'hi',
          ),
        )
      }
    }
  }

  async function getMentorProfileData() {
    const currentPath = window.location.pathname
    const currentMentorId = resolvedMentorIdState || mentorId
    const currentMentorSlug = mentorSlug
    
    // Only use slug-based fetching for kiosk portal
    // For CA portal, only use ID-based fetching
    if (currentMentorId || (currentPath.startsWith('/kiosk') && currentMentorSlug)) {
      setMentorDataLoading(true)
      try {
        let url
        if (currentPath.startsWith('/kiosk') && currentMentorSlug) {
          // Kiosk portal: use kiosk service-provider (same query shape and response as clinic-assistant)
          const isSlugAnId = /^(TCH-|CHRM-|DOC-)/.test(currentMentorSlug)
          const idParam = currentMentorId || (isSlugAnId ? currentMentorSlug : '')
          const slugParam = !idParam ? currentMentorSlug : ''
          url = `/api/secure/kiosk/service-provider?id=${idParam}&slug=${slugParam}&zoneOffset=${encodeURIComponent(
            getTimezoneOffset(),
          )}&appVersion=${appVersion}&platformType=${platformWeb}`
        } else {
          // CA portal: use clinic-assistant service-provider with ID
          url = `/api/secure/clinic-assistant/service-provider?id=${currentMentorId}&slug=&zoneOffset=${encodeURIComponent(
            getTimezoneOffset(),
          )}&appVersion=${appVersion}&platformType=${platformWeb}`
        }

        const response = await getCPDataWithoutParams(url)
        if (response.status === 200 && response?.data) {
          setMentorProfileData(response.data)
          if (response.data.id) {
            setResolvedMentorIdState(response.data.id)
          }
        }
      } catch (error) {
        console.error('Error fetching mentor profile:', error)
      } finally {
        setMentorDataLoading(false)
      }
    }
  }

  async function getAndSetScreenersWithTheirBookedSlots({ serviceProvidersData }) {
    const dataWithBookedSlots = await getScreenersWithTheirBookedSlots({
      screeners: serviceProvidersData,
      selectedDateUtcStartTime,
      selectedDateUtcEndTime,
      clinicId: resolvedClinicId,
    })
    setScreeningMentorsWithBookingData(dataWithBookedSlots)
    dataWithBookedSlots?.forEach((mentor) => {
      setActiveAppointmentMapForMentors((prev) =>
        getUpdatedActiveAppointmentMapAndAnnounceForChanges(
          prev,
          mentor?.id,
          mentor?.name || mentor?.firstName,
          getPrefixRemovedAppointmentNumber(mentor?.bookedSlots?.[0]?.appointmentNumber),
          getPrefixRemovedAppointmentNumber(mentor?.bookedSlots?.[1]?.appointmentNumber),
          'hi',
        ),
      )
    })
  }

  async function getScreeningMentorProfileData() {
    const currentMentorId = resolvedMentorIdState || mentorProfileData?.id
    if (!resolvedClinicId || !currentMentorId) return
    setScreeningMentorDataLoading(true)
    try {
      ///get the active screeners on this room, and then get he profile of all of them
      const presenceMap = await getActiveMentorsInAClinicLocation({
        clinicLocationId: resolvedClinicId,
      })

      setPrimaryMentorCheckedIn(
        Object.values(presenceMap)?.filter((val) => !!val && val?.id === currentMentorId)?.length > 0,
      )
      const presenceMapVals = Object.values(presenceMap)?.filter(
        (val) => !!val && val?.id !== currentMentorId,
      )

      if (presenceMapVals.length < 0) {
        return
      }

      const serviceProvidersData = await getMentorListForClinicAssistant({
        serviceProviders: presenceMapVals,
      })

      await getAndSetScreenersWithTheirBookedSlots({
        serviceProvidersData,
      })
    } catch (error) {
      console.error('Error fetching screening mentor profile:', error)
    } finally {
      setScreeningMentorDataLoading(false)
    }
  }

  async function getOohConfig() {
    const currentMentorId = resolvedMentorIdState || mentorProfileData?.id
    if (!resolvedClinicId || !currentMentorId) return
    try {
      const response = await getCPDataWithoutParams(
        `/api/secure/kiosk/ooh/config/get?mentorId=${currentMentorId}&clinicLocationId=${resolvedClinicId}`,
      )
      if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const oohScreenConfigData = getDataMapFromCommonResponse(response)?.['ooh-screen-config']
        setOohConfig(oohScreenConfigData)
      }
    } catch (error) {
      console.error('Error fetching OOH config:', error)
    }
  }

  async function getOOHContent() {
    const currentMentorId = resolvedMentorIdState || mentorProfileData?.id
    if (!resolvedClinicId || !currentMentorId) {
      setOOHDataLoading(false)
      return
    }
    setOOHDataLoading(true)
    try {
      const response = await getCPDataWithoutParams(
        `/api/secure/kiosk/ooh/msg/all/page?clinicId=${resolvedClinicId}&mentorId=${currentMentorId}&page=0&size=500`,
      )
      if (response.status === 200) {
        setOOHData((response?.data?.content || []).sort((a, b) => a?.orderId - b?.orderId))
      }
    } catch (error) {
      console.error('Error fetching OOH content:', error)
      setOOHData([])
    } finally {
      setOOHDataLoading(false)
    }
  }

  const element = document.getElementById('fullScreenTarget')

  const handleClickFullScreen = () => {
    if (screenfull.isEnabled) {
      screenfull.request(element)
    }
  }

  const handleExitFullScreen = () => {
    if (screenfull.isEnabled) {
      screenfull.exit()
    }
  }

  async function refreshBookedSlots(mentorProfileData) {
    setBookedSlotsInDayLoading(true)
    let promises = [getAndSetBookedSlotsOfSelectedDateForPrimaryMentor(mentorProfileData)]

    promises.push(getScreeningMentorProfileData())

    await Promise.all(promises)
    setBookedSlotsInDayLoading(false)
  }

  useEffect(() => {
    // Don't call APIs that need clinicLocationId until clinic is resolved (avoids null on kiosk before kiosk clinics fetch completes)
    if (!resolvedClinicId) return
    refreshBookedSlots(mentorProfileData) // Initial call
    const intervalId = setInterval(() => {
      getScreeningMentorProfileData()
      refreshBookedSlots(mentorProfileData)
    }, 30000) //60000
    return () => {
      clearInterval(intervalId)
    }
  }, [selectedDate, resolvedMentorIdState, mentorProfileData, resolvedClinicId]) // call every 60 seconds

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeNow(dayjs())
      if (
        (dayjs().format('HH:mm:ss') > '13:00:00' && dayjs().format('HH:mm:ss') < '13:00:15') ||
        (dayjs().format('HH:mm:ss') > '23:00:00' && dayjs().format('HH:mm:ss') < '23:00:15')
      ) {
        window.location.reload()
      }
    }, 1000)
    return () => {
      clearInterval(intervalId)
    }
  }, []) // call every 1 seconds

  useEffect(() => {
    // Only proceed if we have either a mentor ID or slug, and a clinic ID or location slug
    if ((mentorId || mentorSlug) && (clinicId || clinicLocationSlug)) {
      // Fetch mentor profile first - OOH content will be fetched once mentor ID is resolved
      getMentorProfileData()
      // Only call screening/active-mentors API when clinic is resolved (avoids sending null clinicLocationId on kiosk before kiosk clinics fetch completes)
      if (resolvedClinicId) {
        getScreeningMentorProfileData()
      }
      setFlowId(ROLE_CA_FLOWS.FLOW_APPOINTMENT_SCREEN)
    }
  }, [mentorId, mentorSlug, clinicId, clinicLocationSlug, resolvedClinicId])

  // This is self invoking function that listen of the notification
  const setupNotificationListener = async () => {
    const messagingResolve = await messaging
    if (messagingResolve) {
      onMessage(messagingResolve, (payload) => {
        refreshBookedSlots(mentorProfileData)
      })
    }
  }
  setupNotificationListener()

  const thisClinicData =
    assistantData?.associatedClinicsData?.find((item) => item?.id === resolvedClinicId) ||
    (resolvedClinicId === kioskResolvedClinicId ? kioskResolvedClinicData : null)

  return (
    <CorrectLayoutHandler
      thisClinicData={thisClinicData}
      mentorId={resolvedMentorIdState || mentorProfileData?.id}
      mentorDataLoading={mentorDataLoading}
      mentorProfileData={mentorProfileData}
      primaryMentorCheckedIn={primaryMentorCheckedIn}
      OOHData={OOHData}
      bookedSlotsInDay={bookedSlotsInDayForPrimaryMentor}
      bookedSlotsInDayLoading={bookedSlotsInDayLoading}
      screeningMentorDataLoading={screeningMentorDataLoading}
      screeningMentors={screeningMentorsWithBookingData}
      handleRefreshBookedSlots={async () => {
        await refreshBookedSlots(mentorProfileData)
      }}
      timeNow={timeNow}
      screenfull={screenfull}
      handleClickFullScreen={handleClickFullScreen}
      handleExitFullScreen={handleExitFullScreen}
      clinicId={resolvedClinicId}
      selectedDateUtcStartTime={selectedDateUtcStartTime}
      selectedDateUtcEndTime={selectedDateUtcEndTime}
      oohConfig={oohConfig}
    />
  )
}
